import React, { useEffect, useState, useContext } from "react";
import Moment from "react-moment";
import { Spinner } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import { UserContext } from "./UserProvider";
import { getControlRoomDetails, openCloseMeeting } from "../api/xchange-api";
import {
  MEETING_LOCATIONS,
  APP_EVENT_ID,
  HAS_EVENT_TIMEZONE_SUPPORT,
} from "../utils/consts";
import { getPersonalConferanceURL, getNextMeetingRelativeURL } from "../utils/url-utils";
import { MeetingContext } from "./MeetingProvider";

const MeetingControls = ({ companyProfile, currentMeetingId = null }) => {
  const history = useHistory();
  const { userProfile } = useContext(UserContext);
  const [nextMeeting, setNextMeeting] = useState(null);
  const [leftScreen, setLeftScreen] = useState(false);
  const POOLING_INTERVAL = process.env.REACT_APP_POLLING_SERVICE_INTERVAL;
  const [loadingError, setLoadingError] = useState(null);
  const { updateMeetingAndReceptionDetails } = useContext(MeetingContext);

  useEffect(() => {
    let _apiTimer = null;
    const loadControlDetails = async () => {
      if (leftScreen) {
        return;
      }

      try {
        const _controlDetails = await getControlRoomDetails(
          companyProfile.CompanyProfileID,
          userProfile.AttendeeID,
          APP_EVENT_ID
        );
        if (!_controlDetails || !_controlDetails.nextMeeting) {
          setLoadingError("No more meetings scheduled today");
          return;
        } else {
          setNextMeeting(_controlDetails.nextMeeting);
        }
      } catch (error) {
        setLoadingError("Error: unable to load next meeting details.");
        return;
      }

      _apiTimer = setTimeout(() => {
        loadControlDetails();
      }, POOLING_INTERVAL);
    };

    if (userProfile && companyProfile && !leftScreen) {
      loadControlDetails();
    }
    return () => clearTimeout(_apiTimer);
  }, [userProfile, companyProfile, POOLING_INTERVAL, leftScreen]);

  const handleOpenRoom = () => {
    openCloseMeeting(nextMeeting.MeetingDetails.EventID, true);
    updateMeetingAndReceptionDetails();
  };

  const handleNextMeeting = () => {
    if (
      nextMeeting.MeetingDetails.Location === MEETING_LOCATIONS.PERSONAL_ROOM
    ) {
      let meetingURL = getPersonalConferanceURL(
        nextMeeting.MeetingDetails.XchangeMeetingURL
      );
      window.open(meetingURL);
    } else {
      setLeftScreen(true); 
      history.push(
        `/${getNextMeetingRelativeURL(
          nextMeeting.MeetingDetails.XchangeMeetingURL
        )}`
      );
    }
  };

  return (
    <div className="control-card">
      {!nextMeeting && !loadingError && (
        <>
          <div>Loading next meeting details... </div>
          <br />
          <Spinner animation="border" />
        </>
      )}

      {loadingError && <h5>{loadingError}</h5>}

      {nextMeeting && !loadingError && (
        <div className="row">
          <div className="col">
            <h5 className="control-card-header">
              Next meeting
              {nextMeeting.IsNextMeetingInMyMeetingSuite &&
                nextMeeting.MeetingDetails.Location ===
                  MEETING_LOCATIONS.MEETING_SUITE && (
                  <>
                    :&nbsp;
                    {nextMeeting.GuestsArrived ? (
                      <span className="control-card-users-arrived">
                        GUESTS ARRIVED
                      </span>
                    ) : (
                      <span>NOT ARRIVED YET</span>
                    )}
                  </>
                )}
            </h5>
            <div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="currentColor"
                class="bi bi-calendar4-week"
                viewBox="0 0 16 16"
              >
                <path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5zM2 2a1 1 0 0 0-1 1v1h14V3a1 1 0 0 0-1-1H2zm13 3H1v9a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V5z" />
                <path d="M11 7.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zm-3 0a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zm-2 3a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zm-3 0a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1z" />
              </svg>
              &nbsp;
              <span style={{ verticalAlign: "middle" }}>
                <Moment format="h:mma">
                  {HAS_EVENT_TIMEZONE_SUPPORT
                    ? nextMeeting.MeetingDetails.EventStartTimeWithTz
                    : nextMeeting.MeetingDetails.EventStartTime}
                </Moment>{" "}
                -&nbsp;
                <Moment format="h:mma">
                  {HAS_EVENT_TIMEZONE_SUPPORT
                    ? nextMeeting.MeetingDetails.EventEndTimeWithTz
                    : nextMeeting.MeetingDetails.EventEndTime}
                </Moment>
                , &nbsp;
                <Moment format="MMMM DD">
                  {HAS_EVENT_TIMEZONE_SUPPORT
                    ? nextMeeting.MeetingDetails.EventStartTimeWithTz
                    : nextMeeting.MeetingDetails.EventStartTime}
                </Moment>
              </span>
            </div>
            <div style={{ marginLeft: "1.25rem" }}>
              {nextMeeting.MeetingDetails.EventName}
            </div>
            {nextMeeting.MeetingDetails.Location && (
              <div>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  class="bi bi-geo-alt"
                  viewBox="0 0 16 16"
                >
                  <path d="M12.166 8.94c-.524 1.062-1.234 2.12-1.96 3.07A31.493 31.493 0 0 1 8 14.58a31.481 31.481 0 0 1-2.206-2.57c-.726-.95-1.436-2.008-1.96-3.07C3.304 7.867 3 6.862 3 6a5 5 0 0 1 10 0c0 .862-.305 1.867-.834 2.94zM8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10z" />
                  <path d="M8 8a2 2 0 1 1 0-4 2 2 0 0 1 0 4zm0 1a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" />
                </svg>
                &nbsp;
                <span style={{ verticalAlign: "middle" }}>
                  {nextMeeting.MeetingDetails.Location}
                </span>
              </div>
            )}
          </div>
          <div className="col">
            {nextMeeting.IsNextMeetingInMyMeetingSuite &&
              nextMeeting.MeetingDetails.Location ===
                MEETING_LOCATIONS.MEETING_SUITE && (
                <>
                  {nextMeeting.MeetingOpen ? (
                    <button
                      className="btn btn-outline-primary control-card-button control-card-room-open"
                      type="button"
                      disabled
                    >
                      ROOM IS NOW OPEN
                    </button>
                  ) : (
                    <button
                      className="btn btn-outline-primary control-card-button"
                      type="button"
                      onClick={handleOpenRoom}
                    >
                      OPEN ROOM FOR GUESTS
                    </button>
                  )}
                </>
              )}

            {nextMeeting.MeetingDetails.Location ===
              MEETING_LOCATIONS.MEETING_SUITE ||
            nextMeeting.MeetingDetails.Location ===
              MEETING_LOCATIONS.PERSONAL_ROOM ? (
              <>
                {currentMeetingId !== nextMeeting.MeetingDetails.EventID && (
                  <button
                    className="btn btn-outline-primary control-card-button"
                    type="button"
                    onClick={handleNextMeeting}
                  >
                    GO TO NEXT MEETING
                  </button>
                )}
              </>
            ) : (
              <a
                href={nextMeeting.MeetingDetails.XchangeMeetingURL}
                className="btn btn-link control-card-link"
              >
                GO TO NEXT MEETING
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MeetingControls;
